import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  DollarSign,
  FileText,
  LinkIcon,
  History,
  Loader2,
  ArrowUp,
  Bookmark,
  Flame,
  AlertTriangle,
  ChevronLeft,
} from 'lucide-react';
import { api } from '../App';
import Chip, { getBudgetStatusVariant } from '../components/Chip';
import BudgetFinancialsTable from '../components/budget/BudgetFinancialsTable';
import BudgetDocumentsList from '../components/budget/BudgetDocumentsList';
import WorkspaceSelectionModal from '../components/WorkspaceSelectionModal';
import MobileMenuPage from '../components/MobileMenuPage';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { usePageMetadata } from '../contexts/PageMetadataContext';
import {
  BudgetItem,
  BudgetHistoryItem,
  BUDGET_POSITIONS,
  BUDGET_STATUSES,
} from '../types/budget';
import { getPositionColorClasses } from '../utils/positionColors';
import Tabs from '@shared/ui/Tabs';

const tabs = [
  { id: 'financials', label: 'Financials', icon: DollarSign },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'bills', label: 'Related Bills', icon: LinkIcon },
  { id: 'history', label: 'Request History', icon: History },
];

const BudgetItemDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const { setPageMetadata } = usePageMetadata();

  const [item, setItem] = useState<BudgetItem | null>(null);
  const [historyItems, setHistoryItems] = useState<BudgetHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('financials');
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Workspace context
  const urlParams = new URLSearchParams(location.search);
  const fromWorkspace = urlParams.get('workspace');
  const [_workspaceInfo, setWorkspaceInfo] = useState<{ name: string } | null>(null);
  const [_workspaceCanEdit, setWorkspaceCanEdit] = useState(false);

  // Fetch item detail
  useEffect(() => {
    if (!id) return;

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const [detailRes, historyRes] = await Promise.all([
          api.get(`/budget/items/${id}`),
          api.get(`/budget/items/${id}/history`),
        ]);

        if (detailRes.data.success) {
          setItem(detailRes.data.data);
        } else {
          setError('Budget item not found');
        }

        if (historyRes.data.success) {
          setHistoryItems(historyRes.data.data);
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load budget item');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  // Set page metadata
  useEffect(() => {
    if (item) {
      setPageMetadata({
        title: item.item_number,
        description: item.item_title,
        backUrl: fromWorkspace
          ? `/workspaces/${fromWorkspace}?section=budget`
          : '/budget',
      });
    }
    return () => setPageMetadata(null);
  }, [item, setPageMetadata, fromWorkspace]);

  // Workspace info
  useEffect(() => {
    if (!fromWorkspace) return;
    const fetchWs = async () => {
      try {
        const response = await api.get(`/workspaces/${fromWorkspace}`);
        const ws = response.data?.data || response.data;
        if (ws?.name) setWorkspaceInfo({ name: ws.name });
        const memberRes = await api.get(`/workspaces/${fromWorkspace}/user-membership`);
        const membershipData = memberRes.data?.data || memberRes.data;
        if (membershipData?.canEdit) {
          setWorkspaceCanEdit(true);
        }
      } catch (_) { /* workspace info is optional */ }
    };
    fetchWs();
  }, [fromWorkspace]);

  // Tab URL sync
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramKey = isMobile ? 'section' : 'tab';
    const paramValue = params.get(paramKey);
    if (paramValue && tabs.some((t) => t.id === paramValue)) {
      setActiveTab(paramValue);
    }
  }, [location.search, isMobile]);

  // Scroll to top
  useEffect(() => {
    const handleScroll = () => setShowScrollToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      const params = new URLSearchParams(location.search);
      const paramKey = isMobile ? 'section' : 'tab';
      params.set(paramKey, tabId);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [navigate, location, isMobile]
  );

  // Workspace-scoped tracking info
  const wsTracking = item?.workspaces?.find((w) => w.workspace_id === fromWorkspace);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="text-center py-24">
        <p className="text-text-tertiary dark:text-text-dark-tertiary">{error || 'Item not found'}</p>
      </div>
    );
  }

  const statusLabel = BUDGET_STATUSES.find((s) => s.value === item.status)?.label || item.status;

  // Mobile: no section selected -> show menu
  if (isMobile && !urlParams.get('section')) {
    return (
      <>
        <DetailHeader
          item={item}
          statusLabel={statusLabel}
          wsTracking={wsTracking}
          onBookmark={() => setShowBookmarkModal(true)}
        />
        <MobileMenuPage
          menuItems={tabs.map((t) => ({
            id: t.id,
            label: t.label,
            icon: t.icon,
            route: `${location.pathname}?workspace=${fromWorkspace || ''}&section=${t.id}`,
          }))}
        />
        <WorkspaceSelectionModal
          isOpen={showBookmarkModal}
          onClose={() => setShowBookmarkModal(false)}
          entityType="budget_item"
          entityId={String(item.id)}
          entityLabel={item.item_number}
          onSave={() => setShowBookmarkModal(false)}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        item={item}
        statusLabel={statusLabel}
        wsTracking={wsTracking}
        onBookmark={() => setShowBookmarkModal(true)}
      />

      {/* Desktop tabs */}
      {!isMobile && (
        <Tabs
          level="panel"
          ariaLabel="Budget item sections"
          tabs={tabs.map((tab) => ({ id: tab.id, label: tab.label, icon: tab.icon }))}
          activeId={activeTab}
          onChange={handleTabChange}
        />
      )}

      {/* Mobile back to sections */}
      {isMobile && (
        <button
          onClick={() => {
            const params = new URLSearchParams(location.search);
            params.delete('section');
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
          }}
          className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to sections
        </button>
      )}

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {activeTab === 'financials' && (
          <BudgetFinancialsTable financials={item.financials || []} />
        )}

        {activeTab === 'documents' && (
          <BudgetDocumentsList documents={item.documents || []} />
        )}

        {activeTab === 'bills' && (
          <div className="space-y-3">
            {(item.relatedBills || []).length === 0 ? (
              <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary text-sm">
                No related bills.
              </div>
            ) : (
              item.relatedBills!.map((bill) => (
                <Link
                  key={bill.bill_id}
                  to={fromWorkspace ? `/legislation/${bill.bill_id}?workspace=${fromWorkspace}` : `/legislation/${bill.bill_id}`}
                  className="card p-4 flex items-start justify-between hover:shadow-md transition-shadow block"
                >
                  <div>
                    <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">
                      {bill.bill_number}
                    </span>
                    <p className="text-sm text-text-primary dark:text-text-dark-primary mt-1 line-clamp-2">
                      {bill.bill_title}
                    </p>
                    {bill.relation_type && (
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary capitalize mt-1 inline-block">
                        {bill.relation_type.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  {bill.is_placeholder && (
                    <Chip label="Placeholder" variant="warning" size="sm" />
                  )}
                </Link>
              ))
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-3">
            {historyItems.length <= 1 ? (
              <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary text-sm">
                No cross-year history available.
              </div>
            ) : (
              <div className="relative pl-6">
                <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-border-light dark:bg-border-dark" />
                {historyItems.map((h) => {
                  const isCurrent = h.id === item.id;
                  return (
                    <div key={h.id} className="relative mb-4 last:mb-0">
                      <div
                        className={`absolute -left-4 top-2 w-3 h-3 rounded-full border-2 ${
                          isCurrent
                            ? 'bg-primary-600 border-primary-600'
                            : 'bg-white dark:bg-background-dark-secondary border-border-light dark:border-border-dark'
                        }`}
                      />
                      <div
                        className={`card p-4 ${isCurrent ? 'ring-2 ring-primary-500' : 'cursor-pointer hover:shadow-md'}`}
                        onClick={() => {
                          if (!isCurrent) navigate(`/budget/${h.id}`);
                        }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">
                            FY {h.fiscal_year}
                          </span>
                          <Chip label={h.status} variant={getBudgetStatusVariant(h.status)} size="sm" />
                          {isCurrent && (
                            <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                              (current)
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-text-primary dark:text-text-dark-primary">
                          {h.item_number}: {h.item_title}
                        </p>
                        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          {h.dept_name}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bookmark Modal */}
      <WorkspaceSelectionModal
        isOpen={showBookmarkModal}
        onClose={() => setShowBookmarkModal(false)}
        entityType="budget_item"
        entityId={String(item.id)}
        entityLabel={item.item_number}
        onSave={() => setShowBookmarkModal(false)}
      />

      {/* Scroll to Top */}
      {showScrollToTop && (
        <>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="lg:hidden fixed bottom-20 right-4 z-50 bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="hidden lg:block fixed bottom-6 right-6 z-50 bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
};

// Extracted header component
interface DetailHeaderProps {
  item: BudgetItem;
  statusLabel: string;
  wsTracking?: { position?: string; is_hot: boolean; needs_client_review: boolean } | undefined;
  onBookmark: () => void;
}

const DetailHeader: React.FC<DetailHeaderProps> = ({
  item,
  statusLabel,
  wsTracking,
  onBookmark,
}) => (
  <div className="card p-6">
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      {/* Main info */}
      <div className="lg:col-span-3">
        <div className="flex items-center gap-2 mb-1 text-sm text-text-secondary dark:text-text-dark-secondary">
          <span>{item.dept_name || item.deptName}</span>
          <span>•</span>
          <span>FY {item.fiscal_year || item.fiscalYear}</span>
          {item.priority_rank && (
            <>
              <span>•</span>
              <span>Priority #{item.priority_rank}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-lg font-bold text-primary-700 dark:text-primary-400">
            {item.item_number || item.itemNumber}
          </span>
          <Chip label={statusLabel} variant={getBudgetStatusVariant(item.status)} />
          {item.requires_legislation && (
            <Chip label="Requires Legislation" variant="info" size="sm" />
          )}
        </div>
        <h1 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
          {item.item_title || item.itemTitle}
        </h1>
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {item.tags.map((tag) => (
              <Chip key={tag} label={tag} variant="neutral" size="sm" />
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 items-start lg:items-end justify-start">
        {wsTracking && (
          <div className="flex items-center gap-2">
            {wsTracking.position && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPositionColorClasses(wsTracking.position)}`}>
                {BUDGET_POSITIONS.find((p) => p.value === wsTracking.position)?.label || wsTracking.position}
              </span>
            )}
            {wsTracking.is_hot && <Flame className="w-4 h-4 text-orange-500" />}
            {wsTracking.needs_client_review && <AlertTriangle className="w-4 h-4 text-warning-500" />}
          </div>
        )}
        <button
          onClick={onBookmark}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
        >
          <Bookmark className="w-4 h-4" />
          Track
        </button>
      </div>
    </div>
  </div>
);

export default BudgetItemDetailPage;
